/**
 * Path translation between canonical Indonesian slugs (used everywhere
 * in app code) and their English counterparts (used only in /en URLs).
 *
 * Components always pass canonical Indonesian paths to `href()` /
 * `altHref()`. Those helpers delegate here to produce the public URL.
 */

export const MONTH_SLUGS_EN = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

export const MONTH_SLUGS_ID = [
  "januari", "februari", "maret", "april", "mei", "juni",
  "juli", "agustus", "september", "oktober", "november", "desember",
];

/** Top-level segment map: ID → EN */
const ID_TO_EN: Record<string, string> = {
  "cuti-bersama": "joint-leave",
  "libur-nasional": "public-holidays",
  "tanggal-merah": "red-dates",
  "kalender-hijriah": "hijri-calendar",
  "kalender": "calendar",
  "tentang": "about",
  "hari-ini": "today",
  "kontak": "contact",
  "kebijakan-privasi": "privacy-policy",
  "syarat-ketentuan": "terms",
};

const EN_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(ID_TO_EN).map(([k, v]) => [v, k]),
);

/** Tools subpath map */
const TOOL_ID_TO_EN: Record<string, string> = {
  "kalkulator-hari": "day-calculator",
  "cek-weton": "weton-check",
};
const TOOL_EN_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(TOOL_ID_TO_EN).map(([k, v]) => [v, k]),
);

function translateMonthToken(seg: string, dir: "id-en" | "en-id"): string {
  // e.g. "juni-2026" or "7-juni-2026" or "january-2026"
  if (dir === "id-en") {
    const m = /^(\d{1,2}-)?([a-z]+)(-\d{4})$/.exec(seg);
    if (!m) return seg;
    const idx = MONTH_SLUGS_ID.indexOf(m[2]);
    if (idx < 0) return seg;
    return `${m[1] ?? ""}${MONTH_SLUGS_EN[idx]}${m[3]}`;
  }
  const m = /^(\d{1,2}-)?([a-z]+)(-\d{4})$/.exec(seg);
  if (!m) return seg;
  const idx = MONTH_SLUGS_EN.indexOf(m[2]);
  if (idx < 0) return seg;
  return `${m[1] ?? ""}${MONTH_SLUGS_ID[idx]}${m[3]}`;
}

/**
 * Translate a bare month/date segment (no year suffix) when its parent
 * segment is a calendar year, e.g. "mei" or "7-mei" inside
 * /kalender/2026/mei/ or /en/calendar/2026/may/.
 */
function translateBareMonthToken(seg: string, dir: "id-en" | "en-id"): string {
  const fromList = dir === "id-en" ? MONTH_SLUGS_ID : MONTH_SLUGS_EN;
  const toList = dir === "id-en" ? MONTH_SLUGS_EN : MONTH_SLUGS_ID;
  // bare month: "mei" / "may"
  if (/^[a-z]+$/.test(seg)) {
    const idx = fromList.indexOf(seg);
    if (idx < 0) return seg;
    return toList[idx];
  }
  // bare date: "7-mei" / "7-may"
  const m = /^(\d{1,2})-([a-z]+)$/.exec(seg);
  if (!m) return seg;
  const idx = fromList.indexOf(m[2]);
  if (idx < 0) return seg;
  return `${m[1]}-${toList[idx]}`;
}

function isCalendarYearContext(prev: string, prevPrev: string, dir: "id-en" | "en-id"): boolean {
  if (!/^\d{4}$/.test(prev)) return false;
  // Accept either locale's segment because the segment may already
  // have been swapped earlier in the loop (e.g. parts[i-2] becomes
  // "calendar" after id-en swap of "kalender").
  return prevPrev === "kalender" || prevPrev === "calendar";
}

/**
 * Translate a canonical Indonesian path (e.g. "/cuti-bersama/2026")
 * to its English-locale URL form (e.g. "/joint-leave/2026").
 * Numeric year segments and unknown tokens pass through unchanged.
 */
export function translatePathToEn(path: string): string {
  if (!path.startsWith("/")) path = `/${path}`;
  const parts = path.split("/");
  // parts[0] = "" (leading slash)
  for (let i = 1; i < parts.length; i++) {
    const p = parts[i];
    if (!p) continue;
    if (parts[i - 1] === "tools" && TOOL_ID_TO_EN[p]) {
      parts[i] = TOOL_ID_TO_EN[p];
      continue;
    }
    if (ID_TO_EN[p]) {
      parts[i] = ID_TO_EN[p];
      continue;
    }
    // Bare month/date inside /kalender/{year}/...
    if (isCalendarYearContext(parts[i - 1], parts[i - 2] ?? "", "id-en")) {
      const swapped = translateBareMonthToken(p, "id-en");
      if (swapped !== p) {
        parts[i] = swapped;
        continue;
      }
    }
    // try month/date slug
    const swapped = translateMonthToken(p, "id-en");
    if (swapped !== p) parts[i] = swapped;
  }
  return parts.join("/");
}

/**
 * Reverse translation: take a possibly-English URL path and rewrite
 * any English segments back to their canonical Indonesian form.
 * Used by the lang switch so EN→ID navigation reaches the right route.
 */
export function translatePathToId(path: string): string {
  if (!path.startsWith("/")) path = `/${path}`;
  const parts = path.split("/");
  for (let i = 1; i < parts.length; i++) {
    const p = parts[i];
    if (!p) continue;
    if (parts[i - 1] === "tools" && TOOL_EN_TO_ID[p]) {
      parts[i] = TOOL_EN_TO_ID[p];
      continue;
    }
    if (EN_TO_ID[p]) {
      parts[i] = EN_TO_ID[p];
      continue;
    }
    if (isCalendarYearContext(parts[i - 1], parts[i - 2] ?? "", "en-id")) {
      const swapped = translateBareMonthToken(p, "en-id");
      if (swapped !== p) {
        parts[i] = swapped;
        continue;
      }
    }
    const swapped = translateMonthToken(p, "en-id");
    if (swapped !== p) parts[i] = swapped;
  }
  return parts.join("/");
}